import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api'; // Assuming your api.js exports a configured axios instance as 'api' or similar

// Define a default context value
const defaultAuthContextValue = {
  user: null,
  token: null,
  isAuthenticated: false,
  isAdmin: false, // Add isAdmin to default context
  loading: true, // Default to loading initially
  login: async () => { throw new Error('Login function not available outside AuthProvider'); },
  register: async () => { throw new Error('Register function not available outside AuthProvider'); },
  loginWithToken: async () => { throw new Error('loginWithToken not available outside AuthProvider'); },
  logout: () => { throw new Error('Logout function not available outside AuthProvider'); },
};

const AuthContext = createContext(defaultAuthContextValue); // Provide default value

// Read the cached user the last verified /users/me left in localStorage, so a
// returning visitor can render the app shell immediately instead of flashing the
// marketing site while the background verification is in flight.
const readCachedUser = () => {
  try {
    const raw = localStorage.getItem('authUser');
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const initialToken = localStorage.getItem('authToken');
  const cachedUser = readCachedUser();

  const [user, setUser] = useState(cachedUser);
  const [token, setToken] = useState(initialToken);
  // Only block on the network when we have a token but no cached user to render
  // optimistically; otherwise hydrate instantly and verify in the background.
  const [loading, setLoading] = useState(Boolean(initialToken) && !cachedUser);
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(initialToken && cachedUser));
  const [isAdmin, setIsAdmin] = useState(cachedUser?.isAdmin || false); // Add isAdmin state

  // Configure axios to use the token
  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('authToken', token);
      // DEFERRED: setIsAuthenticated(true) is deferred to verifyUserAndLoad or login
      // to ensure user data is also present and isAdmin status is confirmed.
    } else {
      delete api.defaults.headers.common['Authorization'];
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      setUser(null);
      setIsAuthenticated(false);
      setIsAdmin(false);
      // console.log('[AuthContext] Token useEffect: Cleared auth state due to no token.'); // Removed
    }
  }, [token]);

  const verifyUserAndLoad = useCallback(async () => {
    const currentToken = localStorage.getItem('authToken');
    // console.log(`[AuthContext] verifyUserAndLoad: START. Token from localStorage: ${Boolean(currentToken)}`); // Removed
    setLoading(true); // Set loading true at the beginning

    if (currentToken) {
      if (!api.defaults.headers.common['Authorization']) {
        api.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
        // console.log('[AuthContext] verifyUserAndLoad: Set auth header from localStorage token.'); // Removed
      }
      try {
        // console.log('[AuthContext] verifyUserAndLoad: Attempting to fetch /users/me...'); // Removed
        const response = await api.get('/users/me');
        if (response.data) {
          // console.log('[AuthContext] verifyUserAndLoad: /users/me SUCCESS. Response data:', response.data); // Removed
          setUser(response.data);
          localStorage.setItem('authUser', JSON.stringify(response.data));
          
          const adminStatus = response.data.isAdmin || false;
          setIsAdmin(adminStatus);
          setIsAuthenticated(true); // Now also set isAuthenticated
          // console.log(`[AuthContext] verifyUserAndLoad: States SET: isAdmin=${adminStatus}, isAuthenticated=true`); // Removed
          setLoading(false);
          return true;
        } else {
          console.error('[AuthContext] verifyUserAndLoad: /users/me FAILED - No data in response. Clearing auth state.');
          setToken(null); // Clear token, which triggers useEffect to clear other states
        }
      } catch (error) {
        // Only a genuine 401 means the token is no longer valid; transient
        // failures (timeout/network/5xx) should keep the optimistic session so
        // slowness never forces a re-login.
        if (error.response?.status === 401) {
          console.error('[AuthContext] verifyUserAndLoad: /users/me returned 401. Clearing auth state.', error.response?.data?.message || error.message);
          setToken(null); // Clear token, which triggers useEffect to clear other states
        } else {
          console.warn('[AuthContext] verifyUserAndLoad: /users/me transient failure. Keeping optimistic session.', error.response?.data?.message || error.message);
          setLoading(false);
          return Boolean(localStorage.getItem('authToken'));
        }
      }
      setLoading(false);
      return false;
    } else {
      // console.log('[AuthContext] verifyUserAndLoad: No token found. Ensuring states are cleared.'); // Removed
      // Explicitly ensure all auth states are false if no token, even if setLoading(false) is later.
      setUser(null);
      setIsAuthenticated(false);
      setIsAdmin(false);
      localStorage.removeItem('authUser'); // ensure authUser is also cleared
      delete api.defaults.headers.common['Authorization']; // ensure header is also cleared
    }
    setLoading(false);
    // console.log(`[AuthContext] verifyUserAndLoad: END. loading=${false}`); // Removed
    return false;
  }, []); // Removed token from dependencies as it's read directly from localStorage

  /**
   * Signs in with a token the server already issued, which is how the Google and
   * SAML redirects finish: there are no credentials to post, only a token to
   * adopt and a user to load behind it.
   *
   * @param {string} nextToken
   * @returns {Promise<boolean>} whether the token turned out to be usable
   */
  const loginWithToken = useCallback(async (nextToken) => {
    if (!nextToken) return false;

    // Written before verifying, because verifyUserAndLoad reads the token from
    // storage; it clears both again if the token is refused.
    localStorage.setItem('authToken', nextToken);
    api.defaults.headers.common['Authorization'] = `Bearer ${nextToken}`;
    setToken(nextToken);

    return verifyUserAndLoad();
  }, [verifyUserAndLoad]);

  useEffect(() => {
    // console.log("[AuthContext] Mount useEffect: Calling verifyUserAndLoad."); // Removed
    verifyUserAndLoad();
  }, [verifyUserAndLoad]);

  const login = async (email, password) => {
    // console.log(`[AuthContext] login: START for email: ${email}`); // Removed
    setLoading(true);
    try {
      const response = await api.post('/auth/login', { email, password });
      // console.log('[AuthContext] login: /auth/login SUCCESS. Response data:', response.data); // Removed

      if (response.data && response.data.token && response.data.user) {
        // Important: Set token first to trigger the useEffect for header setting
        setToken(response.data.token);
        // console.log('[AuthContext] login: setToken called.'); // Removed

        setUser(response.data.user);
        localStorage.setItem('authUser', JSON.stringify(response.data.user));
        
        const adminStatus = response.data.user.isAdmin || false;
        setIsAdmin(adminStatus);
        setIsAuthenticated(true);
        // console.log(`[AuthContext] login: States SET: isAdmin=${adminStatus}, isAuthenticated=true`); // Removed
      } else {
        console.error('[AuthContext] login: /auth/login FAILED - Invalid response (missing token or user).');
        // Ensure inconsistent state is cleared
        setToken(null);
        setIsAuthenticated(false); // ensure this is false
        setIsAdmin(false); // ensure this is false
        throw new Error(response.data?.message || 'Login failed: Invalid response from server');
      }
      setLoading(false);
      // console.log(`[AuthContext] login: END. loading=${false}`); // Removed
      return response.data;
    } catch (error) {
      console.error('[AuthContext] login: /auth/login FAILED - Exception. Clearing auth state.', error.response?.data?.message || error.message);
      setLoading(false); // Ensure loading is false even on error
      setToken(null); // This will trigger the useEffect to clear everything
      // console.log(`[AuthContext] login: END (Exception). loading=${false}`); // Removed
      throw error;
    }
  };

  const register = async (name, email, password) => {
    setLoading(true);
    try {
      const response = await api.post('/auth/register', { name, email, password });

      // Two shapes come back now: an exempt/verified signup returns a token+user
      // and logs in as before; an unverified signup returns
      // { requiresVerification: true } with no token, and the caller shows a
      // "check your email" panel instead of navigating.
      if (response.data && response.data.token && response.data.user) {
        setToken(response.data.token);

        setUser(response.data.user);
        localStorage.setItem('authUser', JSON.stringify(response.data.user));

        const adminStatus = response.data.user.isAdmin || false;
        setIsAdmin(adminStatus);
        setIsAuthenticated(true);
      } else if (!response.data?.requiresVerification) {
        // Neither a token nor a verification prompt is a genuine failure.
        console.error('[AuthContext] register: /auth/register FAILED - Invalid response (missing token or user).');
        setToken(null);
        setIsAuthenticated(false);
        setIsAdmin(false);
        throw new Error(response.data?.message || 'Registration failed: Invalid response from server');
      }
      setLoading(false);
      return response.data;
    } catch (error) {
      console.error('[AuthContext] register: /auth/register FAILED - Exception. Clearing auth state.', error.response?.data?.message || error.message);
      setLoading(false);
      setToken(null); // This will trigger the useEffect to clear everything
      throw error;
    }
  };

  /**
   * Confirms an email using the token from the verification link and, on
   * success, adopts the returned JWT so the user lands logged in.
   *
   * @param {string} verificationToken
   * @returns {Promise<object>} the server payload (token + user)
   */
  const verifyEmail = async (verificationToken) => {
    setLoading(true);
    try {
      const response = await api.post('/auth/verify-email', { token: verificationToken });
      if (response.data && response.data.token && response.data.user) {
        setToken(response.data.token);
        setUser(response.data.user);
        localStorage.setItem('authUser', JSON.stringify(response.data.user));
        setIsAdmin(response.data.user.isAdmin || false);
        setIsAuthenticated(true);
      }
      setLoading(false);
      return response.data;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  /**
   * Asks the server to send another verification email. Always resolves for a
   * generic response; the server never reveals whether the address exists.
   *
   * @param {string} emailAddress
   */
  const resendVerification = async (emailAddress) => {
    const response = await api.post('/auth/resend-verification', { email: emailAddress });
    return response.data;
  };

  const logout = () => {
    // console.log('[AuthContext] logout: START. Clearing token.'); // Removed
    setToken(null); // This will trigger the useEffect to clear everything
    // No need to manually clear user, isAdmin, isAuthenticated here as useEffect handles it
    // console.log('[AuthContext] logout: END.'); // Removed
  };

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated, isAdmin, login, register, verifyEmail, resendVerification, loginWithToken, logout, refreshUser: verifyUserAndLoad, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
}; 